import React, { useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useEmbedHeight } from "./hooks/useEmbedHeight";
import { Loader2, Inbox, LogIn } from "lucide-react";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { PodcastCard } from "./components/PodcastCard";
import { PodcastDialog } from "./components/PodcastDialog";
import { Footer } from "./components/Footer";
import { SharedEpisodePage } from "./components/SharedEpisodePage";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsOfService } from "./components/TermsOfService";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useUserRuns } from "./hooks/useUserRuns";
import { useShortsAccess } from "./hooks/useShortsAccess";

function EpisodesSection() {
  const { user, loginWithGoogle } = useAuth();
  const { hasAccess: hasShortsAccess } = useShortsAccess(user);
  const { runs, loading, error } = useUserRuns(user);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);

  const INITIAL_LIMIT = 12;
  const PAGE_SIZE = 20;
  const visibleRuns = runs.slice(0, visibleCount);
  const hasMore = runs.length > visibleCount;
  const isExpanded = visibleCount > INITIAL_LIMIT;

  // Always derive the selected run from the live snapshot so updates stream in.
  const selectedRun = selectedRunId
    ? runs.find((r) => r.id === selectedRunId) || null
    : null;

  const handleRunClick = (run) => {
    setSelectedRunId(run.id);
    setDialogOpen(true);
  };

  const showMore = () =>
    setVisibleCount((c) => Math.min(c + PAGE_SIZE, runs.length));
  const showLess = () => setVisibleCount(INITIAL_LIMIT);

  return (
    <>
      <section id="podcasts" className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="flex items-baseline gap-3 flex-wrap mb-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                My Episodes
              </h2>
              {user && !loading && !error && runs.length > 0 && (
                <span className="text-sm sm:text-base text-slate-400 font-medium">
                  {Math.min(visibleCount, runs.length)} / {runs.length}
                </span>
              )}
            </div>
            <p className="text-base sm:text-xl text-slate-400">
              Click on any episode to see the podcast's VALUEBOT analysis{hasShortsAccess ? ' and suggested shorts' : ''}
            </p>
          </div>

          {/* Signed-out state */}
          {!user && (
            <div className="bg-brand-surface border border-brand-surface-3 rounded-2xl p-8 sm:p-12 flex flex-col items-center text-center">
              <LogIn className="w-10 h-10 text-orange-400 mb-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
                Sign in to see your episodes
              </h3>
              <p className="text-sm sm:text-base text-slate-400 mb-5">
                Your processed podcasts will appear here in real time.
              </p>
              <button
                onClick={() => loginWithGoogle().catch(() => {})}
                className="px-5 py-2.5 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors font-medium text-sm sm:text-base"
              >
                Sign in with Google
              </button>
            </div>
          )}

          {/* Loading */}
          {user && loading && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400 mb-3" />
              <p className="text-sm">Loading your episodes…</p>
            </div>
          )}

          {/* Error */}
          {user && !loading && error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
              <p className="text-red-300 font-medium mb-1">Couldn’t load episodes</p>
              <p className="text-red-200/80 text-sm break-words">
                {error?.message || String(error)}
              </p>
            </div>
          )}

          {/* Empty */}
          {user && !loading && !error && runs.length === 0 && (
            <div className="bg-brand-surface border border-brand-surface-3 rounded-2xl p-8 sm:p-12 flex flex-col items-center text-center">
              <Inbox className="w-10 h-10 text-slate-500 mb-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
                No episodes yet
              </h3>
              <p className="text-sm sm:text-base text-slate-400">
                Submit your first podcast above and it will appear here.
              </p>
            </div>
          )}

          {/* Grid */}
          {user && !loading && !error && runs.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {visibleRuns.map((run, idx) => (
                  <div
                    key={run.id}
                    className={
                      idx >= INITIAL_LIMIT
                        ? 'animate-in fade-in slide-in-from-top-4 duration-500'
                        : ''
                    }
                  >
                    <PodcastCard run={run} onClick={() => handleRunClick(run)} />
                  </div>
                ))}
              </div>

              {(hasMore || isExpanded) && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={hasMore ? showMore : showLess}
                    className="group px-6 py-2.5 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 hover:border-orange-500/40 text-slate-300 hover:text-white rounded-full transition-all duration-200 font-medium text-sm flex items-center gap-2"
                  >
                    {hasMore ? 'Show more' : 'Show less'}
                    <span
                      className={`inline-block transition-transform duration-300 ${
                        hasMore ? 'group-hover:translate-y-0.5' : 'rotate-180'
                      }`}
                    >
                      ▾
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <PodcastDialog
        run={selectedRun}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}

function LegacyAppRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/', search, hash }} replace />;
}

function MainPage() {
  const { search } = useLocation();
  const embedded = new URLSearchParams(search).get('embed') === 'true';
  const contentRef = useEmbedHeight(embedded);
  return (
    <div ref={contentRef} className={`App relative ${embedded ? "" : "min-h-screen"} bg-brand-bg overflow-x-hidden`}>
      {/* Ambient background — fixed so it follows the viewport */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[55vw] h-[55vw] bg-orange-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[-10%] w-[60vw] h-[60vw] bg-red-500/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-orange-500/[0.05] rounded-full blur-3xl" />
      </div>

      {/* Page content sits above the ambient layer */}
      <div className="relative z-10">
        <Header embedded={embedded} />
        <Hero embedded={embedded} />
        <EpisodesSection />
        {!embedded && <Footer />}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/app" element={<LegacyAppRedirect />} />
        <Route path="/share/:token" element={<SharedEpisodePage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
